import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

export default function AIForRoofers() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  const faqs = [
    {
      question: 'How does AI help a roofing company get more jobs?',
      answer: 'Storm season and high-wind events can flood your phone lines with calls — many of which come in after hours or when your crew is on a roof. Our AI answers every single one, schedules inspections, and captures homeowner information so you can follow up and close more jobs without missing a beat.'
    },
    {
      question: 'Can the AI handle storm damage inquiries specifically?',
      answer: 'Yes. Your AI is trained on your services, including storm damage assessments, insurance claim assistance, and emergency tarping. It can answer homeowner questions about the insurance process, schedule inspections, and communicate urgency and availability clearly.'
    },
    {
      question: 'Will the AI book roof inspections on my calendar?',
      answer: 'Absolutely. The AI integrates with your calendar and books inspection appointments in real time based on your actual availability. No double bookings, no back-and-forth phone tag — just a confirmed appointment waiting for you.'
    },
    {
      question: 'What if I get a surge of calls after a major storm?',
      answer: 'This is exactly when AI shines. While a human receptionist gets overwhelmed, your AI handles unlimited simultaneous calls with the same quality and professionalism. Every lead gets captured, every caller feels taken care of, even during peak volume.'
    },
    {
      question: 'How is this different from just using voicemail?',
      answer: 'Voicemail loses you leads — most callers hang up rather than leave a message. Our AI has a real, two-way conversation: it answers questions, books appointments, and collects information. Customers feel helped immediately and are far more likely to choose you over a competitor.'
    },
    {
      question: 'What does it cost for a roofing company?',
      answer: 'Plans start at an affordable monthly rate — less than the cost of a single lost roof job. For roofing companies, capturing even one additional job per month from after-hours calls typically delivers 10–20x ROI on the monthly subscription.'
    },
  ];

  return (
    <>
      <SEO
        title="AI for Roofers | 24/7 AI Receptionist & Inspection Booking for Roofing Companies"
        description="Never miss a roofing lead after a storm again. Our AI answers calls, books roof inspections, and handles storm damage inquiries 24/7 — keeping your pipeline full year-round."
        canonical="/ai-for-roofers"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Receptionist for Roofers',
              description: 'AI-powered call answering, lead capture, and inspection scheduling for roofing companies.',
              provider: { '@type': 'Organization', name: 'Dino Nuggets' },
              areaServed: 'United States',
            },
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: faqs.map(f => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
              })),
            },
          },
        ]}
      />
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero */}
        <section className="relative pt-32 pb-20 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0 opacity-40">
            <img
              src="/readdy-assets/roofers-hero-001.jpg"
              alt="AI for Roofers"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/70 to-white/95"></div>

          <div className="relative max-w-4xl mx-auto text-center">
            <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              Built for Roofing Companies
            </span>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              AI for Roofers:<br />Answer Every Call, Book Every Inspection
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              When a storm hits, your phone doesn't stop ringing — but you can't always answer. Our AI receptionist handles every call, books inspections automatically, and captures every lead so you never leave revenue on the table.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get a Free Demo
            </button>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">

            {/* The Problem */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                Why Roofers Lose Jobs Every Single Day
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Roofing is one of the most lead-sensitive businesses out there. Homeowners searching for a roofer are usually urgent — they've got a leak, storm damage, or an insurance adjuster coming in three days. When they call and get voicemail, they move on immediately. A dedicated <Link to="/services/ai-receptionist" className="text-primary hover:underline font-medium">AI receptionist</Link> makes sure that never happens.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Storm seasons are your biggest revenue opportunity of the year — and also the moment when missed calls cost you the most. Your crew is on roofs, you're managing job flow, and the phone rings constantly. Every unanswered call is a job that's likely going to your competitor down the street. <Link to="/ai-for-contractors" className="text-primary hover:underline font-medium">Contractors</Link> in every trade face the same challenge during peak season.
              </p>
              <p className="text-gray-700 leading-relaxed">
                AI for roofers closes this gap entirely. Your AI handles calls 24/7, guides homeowners through the process, books inspection appointments, and sends you organized lead summaries — all without you picking up the phone.
              </p>
            </div>

            {/* What We Do */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                How Our AI Works for Your Roofing Business
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                We combine <Link to="/ai-phone-answering-service" className="text-primary hover:underline font-medium">AI phone answering</Link> with <Link to="/ai-appointment-scheduling" className="text-primary hover:underline font-medium">smart scheduling</Link> and lead follow-up — all trained specifically on your roofing company's services, availability, and service area.
              </p>

              <div className="space-y-6">
                {[
                  {
                    icon: 'ri-phone-line',
                    title: 'Handle Storm Surge Calls Without Stress',
                    body: 'After a major weather event, your AI takes every incoming call simultaneously — no busy signals, no missed opportunities. It answers with your company name, collects homeowner details, explains your inspection process, and gets them booked — whether it\'s 9am or 9pm.'
                  },
                  {
                    icon: 'ri-calendar-check-line',
                    title: 'Book Roof Inspections Directly to Your Calendar',
                    body: 'Homeowners can schedule free roof inspections in real time without you intervening. Your AI checks your actual availability and books the slot, sends confirmation texts and emails, and keeps your schedule organized through peak season.'
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: 'Guide Homeowners Through the Insurance Process',
                    body: 'Many homeowners don\'t know where to start with insurance claims. Your AI can explain the process, set expectations on timelines, and capture the right information (adjuster appointment dates, insurance provider, etc.) so you show up prepared.'
                  },
                  {
                    icon: 'ri-message-3-line',
                    title: 'Follow Up Automatically on Every Lead',
                    body: 'Our AI sends automated follow-up messages to homeowners who requested inspections but haven\'t confirmed, leads from your website chatbot, and post-inspection follow-ups for estimates you\'ve sent — dramatically improving your close rate.'
                  },
                ].map(item => (
                  <div key={item.title} className="bg-gray-50 p-6 rounded-lg flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg">
                      <i className={`${item.icon} text-2xl text-primary`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                The Real Cost of Missed Roofing Calls
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { stat: '$8,500+', label: 'Average value of a residential roofing job — each missed call could mean missing that revenue.' },
                  { stat: '78%', label: 'of homeowners hire the first roofer who responds professionally to their inquiry.' },
                  { stat: '24/7', label: 'Storm damage doesn\'t follow business hours — and neither does your AI receptionist.' },
                ].map(item => (
                  <div key={item.stat} className="border border-gray-200 rounded-lg p-6 text-center">
                    <div className="text-4xl font-bold text-primary mb-3">{item.stat}</div>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Services */}
            <div className="mb-16 bg-gradient-to-br from-gray-50 to-primary/5 p-8 rounded-xl">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                Complete AI Toolkit for Roofing Companies
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { to: '/services/ai-receptionist', icon: 'ri-customer-service-2-line', title: 'AI Receptionist', desc: 'Full virtual front desk — handles calls, texts, and web inquiries together.' },
                  { to: '/ai-phone-answering-service', icon: 'ri-phone-line', title: 'Phone Answering', desc: 'Professional AI that answers calls in your company name, 24/7.' },
                  { to: '/ai-appointment-scheduling', icon: 'ri-calendar-2-line', title: 'Inspection Scheduling', desc: 'Automatic calendar booking for roof inspections and estimates.' },
                  { to: '/services/ai-chatbot', icon: 'ri-chat-smile-3-line', title: 'Website Chatbot', desc: 'Capture website leads around the clock with an intelligent chatbot.' },
                ].map(item => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="bg-white p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                  >
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-2xl text-primary`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Other Industries */}
            <div className="mb-16">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                See How We Help Other Field Service Businesses
              </h2>
              <div className="flex flex-wrap gap-3">
                <Link to="/ai-for-contractors" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Contractors</Link>
                <Link to="/ai-for-landscapers" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Landscapers</Link>
                <Link to="/ai-for-rv-parks" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for RV Parks &amp; Campgrounds</Link>
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <i className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-primary to-secondary text-white p-12 rounded-2xl text-center">
              <h2 className="font-display text-3xl font-bold mb-4">Ready to Capture More Roofing Jobs?</h2>
              <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
                See how our AI works for your roofing company in a free 15-minute demo. Setup takes less than 48 hours and we handle everything.
              </p>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Schedule My Free Demo
              </button>
            </div>

          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />
        <ServiceModal isOpen={isServiceModalOpen} onClose={() => setIsServiceModalOpen(false)} title={selectedService} />
        <IndustriesModal isOpen={isIndustriesModalOpen} onClose={() => setIsIndustriesModalOpen(false)} />
        <HowItWorksModal isOpen={isHowItWorksModalOpen} onClose={() => setIsHowItWorksModalOpen(false)} />
        <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
      </div>
    </>
  );
}
